import { useEffect, useRef, useState } from 'react'
import { wavFileFromRecording } from '../../lib/audioSample'
import { trackMicRecordingUsed } from '../../analytics/posthog'
import { useT } from '../../i18n/useT'
import { LoadingSpinner } from '../ui/LoadingSpinner'

/** In-browser voice recorder with a live input-level meter, so the user can
 * see their voice is actually being picked up. The recording converts to a
 * 16 kHz mono WAV locally and joins the normal upload flow — the 30-minute
 * cap keeps the file far below the 75 MB upload limit. */

const MAX_RECORD_SECONDS = 30 * 60
/** RMS below this counts as silence; 4 s of it shows the "can't hear you" hint */
const SILENCE_RMS = 0.015
const SILENCE_HINT_MS = 4000
const METER_BARS = 48

const MIME_CANDIDATES = [
  'audio/mp4',
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
]

type Phase = 'idle' | 'recording' | 'processing'

function formatClock(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function recordingFilename(): string {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return `recording-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}.wav`
}

interface MicRecorderProps {
  disabled?: boolean
  onRecorded: (file: File) => void
}

export function MicRecorder({ disabled, onRecorded }: MicRecorderProps) {
  const t = useT()
  const [phase, setPhase] = useState<Phase>('idle')
  const [elapsed, setElapsed] = useState(0)
  // Stored as a catalog key so the message follows the UI language
  const [error, setError] = useState<string | null>(null)
  const [silent, setSilent] = useState(false)

  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const discardRef = useRef(false)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Level meter machinery
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const rafRef = useRef<number | null>(null)
  const levelsRef = useRef<number[]>([])
  const lastLoudRef = useRef(0)
  const silentShownRef = useRef(false)

  const supported =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined'

  useEffect(() => {
    return () => {
      // Unmount while recording: drop everything and release the mic
      discardRef.current = true
      if (timerRef.current) clearInterval(timerRef.current)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop()
      }
      streamRef.current?.getTracks().forEach((t) => t.stop())
      void audioCtxRef.current?.close().catch(() => {})
    }
  }, [])

  if (!supported) return null

  function cleanupStream() {
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    recorderRef.current = null
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    void audioCtxRef.current?.close().catch(() => {})
    audioCtxRef.current = null
    analyserRef.current = null
  }

  function startMeter(stream: MediaStream) {
    try {
      const ctx = new AudioContext()
      // iOS starts contexts suspended even inside a tap handler sometimes
      void ctx.resume().catch(() => {})
      const source = ctx.createMediaStreamSource(stream)
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 512
      source.connect(analyser)
      audioCtxRef.current = ctx
      analyserRef.current = analyser
      levelsRef.current = new Array(METER_BARS).fill(0)
      lastLoudRef.current = Date.now()
      silentShownRef.current = false

      const data = new Uint8Array(analyser.fftSize)
      const draw = () => {
        const a = analyserRef.current
        if (!a) return
        const canvas = canvasRef.current
        if (!canvas) {
          // First frames can beat React's commit of the canvas — keep looping
          rafRef.current = requestAnimationFrame(draw)
          return
        }
        a.getByteTimeDomainData(data)
        let sum = 0
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128
          sum += v * v
        }
        const rms = Math.sqrt(sum / data.length)
        const levels = levelsRef.current
        levels.push(Math.min(1, rms * 3))
        if (levels.length > METER_BARS) levels.shift()

        // "Can't hear you" hint — state flips only on transitions
        const now = Date.now()
        if (rms > SILENCE_RMS) lastLoudRef.current = now
        const isSilent = now - lastLoudRef.current > SILENCE_HINT_MS
        if (isSilent !== silentShownRef.current) {
          silentShownRef.current = isSilent
          setSilent(isSilent)
        }

        // HiDPI-aware redraw
        const dpr = window.devicePixelRatio || 1
        const w = canvas.clientWidth
        const h = canvas.clientHeight
        if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
          canvas.width = w * dpr
          canvas.height = h * dpr
        }
        const g = canvas.getContext('2d')
        if (g) {
          g.clearRect(0, 0, canvas.width, canvas.height)
          // Canvas has no currentColor, so the bars are painted with the
          // element's own computed `color` — which follows the theme like
          // anything else. The fallback reads the token rather than naming a
          // blue: a literal here would stay light-mode blue forever.
          const style = getComputedStyle(canvas)
          g.fillStyle =
            style.getPropertyValue('color') ||
            getComputedStyle(document.documentElement).getPropertyValue('--color-primary')
          const barW = canvas.width / METER_BARS
          for (let i = 0; i < levels.length; i++) {
            const barH = Math.max(2 * dpr, levels[i] * canvas.height)
            const x = i * barW
            const y = (canvas.height - barH) / 2
            g.fillRect(x + barW * 0.2, y, barW * 0.6, barH)
          }
        }
        rafRef.current = requestAnimationFrame(draw)
      }
      rafRef.current = requestAnimationFrame(draw)
    } catch {
      // Meter is best-effort — recording works without it
    }
  }

  async function start() {
    setError(null)
    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
      })
    } catch {
      setError('mic.denied')
      return
    }

    const mimeType = MIME_CANDIDATES.find((m) => MediaRecorder.isTypeSupported(m))
    let recorder: MediaRecorder
    try {
      recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream)
    } catch {
      stream.getTracks().forEach((track) => track.stop())
      setError('mic.unsupported')
      return
    }

    chunksRef.current = []
    discardRef.current = false
    const recordingStartedAt = Date.now()
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data)
    }
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' })
      chunksRef.current = []
      cleanupStream()
      setSilent(false)
      if (discardRef.current) {
        setPhase('idle')
        setElapsed(0)
        return
      }
      setPhase('processing')
      void wavFileFromRecording(blob, recordingFilename()).then((file) => {
        setPhase('idle')
        setElapsed(0)
        if (file) {
          trackMicRecordingUsed(Math.round((Date.now() - recordingStartedAt) / 1000))
          onRecorded(file)
        } else {
          setError('mic.processFailed')
        }
      })
    }

    streamRef.current = stream
    recorderRef.current = recorder
    recorder.start(1000)
    setPhase('recording')
    setElapsed(0)
    startMeter(stream)

    const startedAt = Date.now()
    timerRef.current = setInterval(() => {
      const seconds = Math.floor((Date.now() - startedAt) / 1000)
      setElapsed(seconds)
      // Hard cap keeps the resulting WAV far below the upload limit
      if (seconds >= MAX_RECORD_SECONDS && recorderRef.current?.state === 'recording') {
        recorderRef.current.stop()
      }
    }, 500)
  }

  function stop() {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
  }

  function cancel() {
    discardRef.current = true
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
  }

  if (phase === 'recording') {
    return (
      <div className="border-2 border-primary/40 bg-primary/5 rounded-xl p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <span className="inline-flex items-center gap-2 text-sm font-medium text-primary-hover">
            <span className="w-2.5 h-2.5 rounded-full bg-primary animate-pulse" aria-hidden="true" />
            {t('mic.recording')}
          </span>
          <span className="text-sm text-text tabular-nums">
            {formatClock(elapsed)}
            <span className="text-text-muted"> / {formatClock(MAX_RECORD_SECONDS)}</span>
          </span>
        </div>

        {/* Live input level — proof the mic hears you */}
        <canvas
          ref={canvasRef}
          className="mt-3 w-full h-10 text-primary"
          aria-label={t('mic.level')}
        />
        {silent && (
          <p className="mt-1 text-xs text-error text-center">{t('mic.noSound')}</p>
        )}

        <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={stop}
            className="px-5 py-2 bg-primary text-on-accent text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors"
          >
            {t('mic.stop')}
          </button>
          <button
            type="button"
            onClick={cancel}
            className="px-4 py-2 text-sm text-text-secondary hover:text-text rounded-lg hover:bg-surface-hover transition-colors"
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    )
  }

  if (phase === 'processing') {
    return (
      <div className="border-2 border-border rounded-xl p-4 flex items-center justify-center gap-3">
        <LoadingSpinner size="sm" />
        <p className="text-sm text-text-secondary">{t('mic.preparing')}</p>
      </div>
    )
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => void start()}
        disabled={disabled}
        className="w-full border-2 border-dashed border-border rounded-xl p-4 flex items-center justify-center gap-3 cursor-pointer hover:border-primary/60 hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
      >
        <span className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
          <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
            />
          </svg>
        </span>
        <span className="text-left">
          <span className="block text-sm font-medium text-text">{t('mic.record')}</span>
          <span className="block text-xs text-text-muted mt-0.5">{t('mic.hint')}</span>
        </span>
      </button>
      {error && <p className="mt-1.5 text-xs text-error">{t(error)}</p>}
    </div>
  )
}
